import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandItem, CommandList,
} from '@/components/ui/command';

interface SearchResult {
  id: string;
  label: string;
  sub?: string;
  type: 'client' | 'template';
  href: string;
}

export function GlobalSearch() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const search = useCallback(async (q: string) => {
    const userId = session?.user?.id;
    if (!userId || q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const like = `%${q.trim()}%`;
    const [clientsRes, templatesRes] = await Promise.all([
      (supabase as any)
        .from('clients')
        .select('id, first_name, last_name, email, phone')
        .eq('owner_id', userId)
        .eq('archived', false)
        .or(`first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like},phone.ilike.${like}`)
        .limit(5),
      supabase
        .from('templates')
        .select('id, name')
        .eq('owner_id', userId)
        .ilike('name', like)
        .limit(5),
    ]);
    const clientResults: SearchResult[] = (clientsRes.data ?? []).map((c: any) => ({
      id: c.id,
      label: [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'Client',
      sub: c.email ?? undefined,
      type: 'client' as const,
      href: `/clients/${c.id}`,
    }));
    const templateResults: SearchResult[] = (templatesRes.data ?? []).map((t: any) => ({
      id: t.id,
      label: t.name,
      type: 'template' as const,
      href: `/editor/${t.id}`,
    }));
    setResults([...clientResults, ...templateResults]);
    setLoading(false);
  }, [session?.user?.id]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!query.trim()) { setResults([]); return; }
    timerRef.current = setTimeout(() => search(query), 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [query, search]);

  const handleSelect = (result: SearchResult) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    navigate(result.href);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className="flex items-center gap-2 h-8 px-3 rounded-xl bg-secondary/60 border border-border/40 text-sm text-muted-foreground cursor-text w-44 md:w-56 transition-all"
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        >
          <Search size={14} className="shrink-0" />
          <span className="truncate">{query || 'Rechercher…'}</span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start" sideOffset={6}>
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3 gap-2">
            <Search size={14} className="shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Clients, templates…"
              className="flex-1 py-2.5 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
              autoFocus
            />
          </div>
          <CommandList>
            {!loading && query.trim().length >= 2 && results.length === 0 && (
              <CommandEmpty>Aucun résultat</CommandEmpty>
            )}
            {query.trim().length < 2 && (
              <CommandEmpty className="py-3 text-xs">Tapez au moins 2 caractères</CommandEmpty>
            )}
            {results.filter(r => r.type === 'client').length > 0 && (
              <CommandGroup heading="Clients">
                {results.filter(r => r.type === 'client').map(r => (
                  <CommandItem key={r.id} onSelect={() => handleSelect(r)} className="cursor-pointer">
                    <div>
                      <p className="text-sm font-medium">{r.label}</p>
                      {r.sub && <p className="text-xs text-muted-foreground">{r.sub}</p>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.filter(r => r.type === 'template').length > 0 && (
              <CommandGroup heading="Templates">
                {results.filter(r => r.type === 'template').map(r => (
                  <CommandItem key={r.id} onSelect={() => handleSelect(r)} className="cursor-pointer">
                    <p className="text-sm font-medium">{r.label}</p>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
